import { motion } from "framer-motion";
import { Scale, CheckCircle2, AlertCircle, Gavel } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SEO, breadcrumbJsonLd } from "@/lib/seo";

const Terms = () => {
    const { t } = useTranslation();

    const sections = [
        { key: "service" as const, items: ["i1", "i2", "i3"] },
        { key: "ip" as const, items: ["i1", "i2", "i3"] },
        { key: "payment" as const, items: ["i1", "i2", "i3"] },
        { key: "disclaimer" as const, items: ["i1", "i2", "i3"] },
    ];

    return (
        <>
            <SEO
                title={t("meta.terms_title")}
                description={t("meta.terms_description")}
                path="/terms"
                jsonLd={breadcrumbJsonLd([
                    { name: t("nav.home"), path: "/" },
                    { name: t("nav.terms"), path: "/terms" },
                ])}
            />
            <div className="pt-28 sm:pt-32 pb-16 sm:pb-24 min-h-[100svh]">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mb-24">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-black uppercase tracking-widest mb-8"
                        >
                            <Gavel size={14} />
                            {t("terms.badge")}
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 tracking-tight"
                        >
                            {t("terms.title_prefix")} <span className="text-muted-foreground">{t("terms.title_highlight")}</span>
                        </motion.h1>
                        <p className="text-xl md:text-2xl text-muted-foreground font-medium leading-relaxed max-w-2xl">
                            {t("terms.subtitle")}
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12">
                        {sections.map((section, idx) => (
                            <motion.div
                                key={section.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + idx * 0.1 }}
                                className="space-y-8"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-primary">
                                        <Scale size={20} />
                                    </div>
                                    <h2 className="text-2xl font-black text-white">
                                        {t(`terms.sections.${section.key}.title`)}
                                    </h2>
                                </div>
                                <div className="bento-card p-10 space-y-6">
                                    {section.items.map((item, i) => (
                                        <div key={i} className="flex items-start gap-4 group">
                                            <div className="mt-1.5">
                                                <CheckCircle2 size={16} className="text-primary/50 group-hover:text-primary transition-colors" />
                                            </div>
                                            <p className="text-lg text-muted-foreground font-medium leading-relaxed group-hover:text-white transition-colors">
                                                {t(`terms.sections.${section.key}.${item}`)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.6 }}
                        className="mt-24 p-12 md:p-16 rounded-[3rem] bg-amber-500/5 border border-amber-500/10 flex items-start gap-8"
                    >
                        <div className="w-14 h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-500 shrink-0">
                            <AlertCircle size={28} />
                        </div>
                        <div>
                            <h3 className="text-2xl font-black text-white mb-4">{t("terms.note_title")}</h3>
                            <p className="text-lg text-amber-200/60 font-medium leading-relaxed">
                                {t("terms.note_body")}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default Terms;
